// The menu data model. One declaration per action, rendered by MenuHost into the
// DOM and (Phase 5) by VrMenu3D onto a world-space panel — same array, two
// surfaces, so a headset user and a phone user cannot be offered different
// things.
//
// `labelKey: DictKey` rather than `label: string` is the point: an untranslated
// entry is a compile error, not a Portuguese string that quietly ships to an
// English-speaking buyer.

import type { DictKey, Locale, TFn } from '../../i18n';

/** Who is allowed to see an entry. `?operator=1` unlocks operator; ?edit=1 /
 *  ?embed=1 imply it, because an operator is authoring. */
export type MenuAudience = 'buyer' | 'operator' | 'editor';

/** Where an entry can appear. Keeps VR-only affordances (submenus for rooms and
 *  variants, which the DOM renders as chips) out of the DOM rail, and DOM-only
 *  ones (QR, WhatsApp — both meaningless in a headset) out of VR. */
export type MenuSurface = 'dom' | 'vr' | 'both';

/** Priority tier. `primary` competes for rail slots (how many depends on the
 *  breakpoint); `secondary` fills whatever is left; `overflow` never promotes. */
export type MenuTier = 'primary' | 'secondary' | 'overflow';

/** Section headings in the overflow sheet. */
export type MenuGroup = 'navegar' | 'ver' | 'compartilhar' | 'acessibilidade' | 'ferramentas';

/** Where the entry is drawn. Most go in the rail; the VR button is a corner FAB
 *  and the before/after + compare pills belong to the bottom bar. */
export type MenuRender = 'rail' | 'fab' | 'bottombar';

export interface AudienceFlags {
  operator: boolean;
  editor: boolean;
  embedded: boolean;
}

export interface MenuContext {
  readonly audience: AudienceFlags;
  /** The tour's rooms, for gates that depend on what the CONTENT carries rather
   *  than on what the browser supports — the floor plan and the dollhouse are
   *  useless without floorplan geometry, which a portal-built tour lacks. */
  readonly rooms: readonly { floorplan?: { shape?: unknown } }[];
  readonly locale: Locale;
  readonly t: TFn;
  /** Ask the host to re-read isVisible / isEnabled / isPressed. Pass an id to
   *  refresh one entry, or nothing to refresh all. Both MenuHost and VrMenu3D
   *  listen, so a change on either surface updates the other. */
  invalidate(id?: string): void;
  isXrActive(): boolean;
}

export interface MenuItem {
  /** Stable, surface-independent. Also the analytics key. */
  id: string;

  /**
   * MIGRATION SCAFFOLD. While a feature module still wires itself up with
   * `document.getElementById('narrator-button')`, MenuHost renders the button
   * with this id and attaches no handler of its own — the module's existing
   * listener keeps working and nothing has to change in the same commit that
   * deletes the hardcoded HTML.
   *
   * Removed per-module in Phase 3, when the module returns `activate` instead.
   * tests/menu-registry.test.ts pins the set, because a typo here produces a
   * silently dead button rather than an error.
   */
  legacyDomId?: string;

  labelKey: DictKey;
  ariaKey?: DictKey;
  /** Used when isPressed() is true, for toggles whose label flips ("Turn off…"). */
  ariaPressedKey?: DictKey;
  titleKey?: DictKey;

  /**
   * Overrides the three keys above when what the entry OPENS depends on the
   * tour's content. `buildMenu` resolves this once, at compose time, into plain
   * `labelKey`/`ariaKey`/`titleKey` — so every renderer (MenuHost, VrMenu3D,
   * the sheet) keeps reading static keys and needs no knowledge of it.
   *
   * The floor plan is the case this exists for: the same panel draws an SVG
   * plan when the tour carries geometry and a plain room list when it does not,
   * and labelling the list "Planta" told the visitor the wrong thing.
   *
   * Still DictKeys, so an untranslated branch is a compile error.
   */
  labelsFor?: (ctx: MenuContext) => {
    labelKey: DictKey;
    ariaKey?: DictKey;
    titleKey?: DictKey;
  };

  /** Emoji today. Swapping in an icon set later touches only the renderer. */
  icon: string;

  group: MenuGroup;
  tier: MenuTier;
  /** Sort key within a tier; lower first. */
  priority: number;
  audience: MenuAudience;
  surfaces: MenuSurface;
  renderAs?: MenuRender;
  /**
   * Single character, matched case-insensitively against a bare keypress.
   * Only honoured for entries that own their `activate` — a legacy entry's
   * module still registers its own keydown, and binding it twice would toggle
   * twice per press.
   */
  shortcut?: string;

  /** Rendered with `.hidden` and left for its module to reveal — currently only
   *  Follow, which appears once a copresence peer joins. Retired in Phase 3 in
   *  favour of `isVisible`. */
  startsHidden?: boolean;

  /** Static gate, evaluated once at mount — capability or platform. Returning
   *  false means the entry is never created, which is what lets a module's
   *  existing `if (!btn) return;` branch handle an unsupported browser with no
   *  code change. */
  isAvailable?(ctx: MenuContext): boolean;
  /** Dynamic visibility, re-read on invalidate — e.g. Follow only exists once a
   *  copresence peer joins. */
  isVisible?(ctx: MenuContext): boolean;
  /** Dynamic enablement; greys out rather than unmounting, so the control does
   *  not jump around — e.g. Compare without a "before" variant in this room. */
  isEnabled?(ctx: MenuContext): boolean;
  /** Toggle state → aria-pressed. Absent means this is an action, not a toggle. */
  isPressed?(ctx: MenuContext): boolean;

  /** Absent during migration; the legacy module still owns the click. */
  activate?(ctx: MenuContext): void | Promise<void>;

  /** How the entry behaves on the world-space VR panel (Phase 5). */
  vr?: {
    kind: 'toggle' | 'action' | 'submenu';
    submenu?: 'rooms' | 'variants' | 'time' | 'language';
  };
}

export const MENU_GROUP_ORDER: readonly MenuGroup[] = [
  'navegar',
  'ver',
  'compartilhar',
  'acessibilidade',
  'ferramentas',
];

export const MENU_GROUP_LABEL_KEY: Record<MenuGroup, DictKey> = {
  navegar: 'menu.group.navegar',
  ver: 'menu.group.ver',
  compartilhar: 'menu.group.compartilhar',
  acessibilidade: 'menu.group.acessibilidade',
  ferramentas: 'menu.group.ferramentas',
};
